// src/pages/admin/admin-asignaciones.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  updateDoc,
  writeBatch,
  getDoc
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useUser } from '../../context/UserContext';
import Swal from 'sweetalert2';
import UserNav from '../../components/navbar/user-nav';
import Breadcrumb from '../../components/Breadcrumb';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import {
  Users,
  Building2,
  Home,
  Link as LinkIcon,
  AlertCircle,
  CheckCircle,
  ArrowRightLeft,
  Search,
  Zap,
  Save
} from 'lucide-react';

interface Miembro {
  id: string;
  nombre: string;
  correo: string;
  telefono: string;
  tipo: 'asesor' | 'asociado';
  inmobiliaria_doc_id?: string;
  inmobiliaria_doc_ids?: string[];
  inmobiliaria_nombre?: string;
}

interface Inmobiliaria {
  id: string;
  nombre: string;
  ciudad?: string;
  telefono?: string;
  correo?: string;
}

interface Propiedad {
  id: string;
  titulo?: string;
  nombre?: string;
  list_number?: string;
  ciudad?: string;
  precio?: number;
  tipo?: string;
  afiliado_doc_id?: string;
  asociado_doc_id?: string;
  inmobiliaria_doc_id?: string;
}

export default function AdminAsignaciones() {
  const { user } = useUser();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tabActiva, setTabActiva] = useState<'miembros-inmo' | 'props-miembros' | 'miembros-vacio' | 'props-inmo'>('miembros-inmo');

  // Estados para datos
  const [miembrosSinInmo, setMiembrosSinInmo] = useState<Miembro[]>([]);
  const [propiedadesSinDueno, setPropiedadesSinDueno] = useState<Propiedad[]>([]);
  const [propiedadesSinInmo, setPropiedadesSinInmo] = useState<Propiedad[]>([]);
  const [inmobiliariasVacias, setInmobiliariasVacias] = useState<Inmobiliaria[]>([]);
  const [todasInmobiliarias, setTodasInmobiliarias] = useState<Inmobiliaria[]>([]);
  const [todosAfiliados, setTodosAfiliados] = useState<Miembro[]>([]);
  const [todosAsociados, setTodosAsociados] = useState<Miembro[]>([]);

  // Estados para búsqueda
  const [busqueda, setBusqueda] = useState('');

  // Estadísticas
  const [stats, setStats] = useState({
    afiliadosSinInmo: 0,
    asociadosSinInmo: 0,
    propsSinDueno: 0,
    inmobiliariasVacias: 0,
    asignadosHoy: 0
  });

  useEffect(() => {
    if (!user || !user.tipo?.includes('admin')) {
      Swal.fire({
        icon: 'error',
        title: 'Acceso denegado',
        text: 'No tienes permiso para acceder a esta sección.',
        confirmButtonColor: '#dc3545'
      });
      navigate('/admin-dashboard');
      return;
    }

    cargarDatos();
  }, [user, navigate]);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      // Cargar todas las inmobiliarias
      const inmosSnap = await getDocs(collection(db, 'inmobiliaria'));
      const inmos: Inmobiliaria[] = inmosSnap.docs.map(d => ({
        id: d.id,
        nombre: d.data().nombre || 'Sin nombre',
        ciudad: d.data().ciudad || '',
        telefono: d.data().telefono || '',
        correo: d.data().correo || ''
      }));
      setTodasInmobiliarias(inmos);

      // Cargar afiliados sin inmobiliaria (colección 'asesor')
      const afiliadosSnap = await getDocs(collection(db, 'asesor'));
      const afiliadosSinInmo: Miembro[] = [];
      const afiliadosConInmo: Miembro[] = [];

      afiliadosSnap.docs.forEach(d => {
        const data = d.data();
        const miembro: Miembro = {
          id: d.id,
          nombre: data.nombre || 'Sin nombre',
          correo: data.correo || '',
          telefono: data.telefono || '',
          tipo: 'asesor',
          inmobiliaria_doc_id: data.inmobiliaria_doc_id || '',
          inmobiliaria_doc_ids: data.inmobiliaria_doc_ids || [],
          inmobiliaria_nombre: data.inmobiliaria_nombre || ''
        };

        // Verificar usando el array field inmobiliaria_doc_ids (igual que ver-admin-afiliado.tsx)
        if (!data.inmobiliaria_doc_ids || !Array.isArray(data.inmobiliaria_doc_ids) || data.inmobiliaria_doc_ids.length === 0) {
          afiliadosSinInmo.push(miembro);
        } else {
          afiliadosConInmo.push(miembro);
        }
      });

      // Cargar asociados sin inmobiliaria
      const asociadosSnap = await getDocs(collection(db, 'asociado'));
      const asociadosSinInmo: Miembro[] = [];
      const asociadosConInmo: Miembro[] = [];

      asociadosSnap.docs.forEach(d => {
        const data = d.data();
        const miembro: Miembro = {
          id: d.id,
          nombre: data.nombre || 'Sin nombre',
          correo: data.correo || '',
          telefono: data.telefono || '',
          tipo: 'asociado',
          inmobiliaria_doc_id: data.inmobiliaria_doc_id || '',
          inmobiliaria_nombre: data.inmobiliaria_nombre || ''
        };

        if (!data.inmobiliaria_doc_id || data.inmobiliaria_doc_id === '') {
          asociadosSinInmo.push(miembro);
        } else {
          asociadosConInmo.push(miembro);
        }
      });

      setMiembrosSinInmo([...afiliadosSinInmo, ...asociadosSinInmo]);
      setTodosAfiliados([...afiliadosSinInmo, ...afiliadosConInmo]);
      setTodosAsociados([...asociadosSinInmo, ...asociadosConInmo]);

      // Cargar propiedades sin dueño
      const propsSnap = await getDocs(collection(db, 'propiedades'));
      const propsSinDueno: Propiedad[] = [];
      const propsSinInmo: Propiedad[] = [];

      propsSnap.docs.forEach(d => {
        const data = d.data();

        // Verificar si tiene asesores asignados (asesor_doc_ids es un array)
        const tieneAsesores = data.asesor_doc_ids && Array.isArray(data.asesor_doc_ids) && data.asesor_doc_ids.length > 0;

        const prop: Propiedad = {
          id: d.id,
          titulo: data.titulo || data.nombre || 'Sin título',
          nombre: data.nombre || '',
          list_number: data.list_number || '',
          ciudad: data.ciudad || '',
          precio: data.precio || 0,
          tipo: data.tipo || '',
          afiliado_doc_id: data.afiliado_doc_id || '',
          asociado_doc_id: data.asociado_doc_id || '',
          inmobiliaria_doc_id: data.inmobiliaria_doc_id || ''
        };

        // Propiedades sin dueño (sin asesores en asesor_doc_ids)
        if (!tieneAsesores) {
          propsSinDueno.push(prop);
        }

        // Propiedades sin inmobiliaria
        if (!data.inmobiliaria_doc_id || data.inmobiliaria_doc_id === '') {
          propsSinInmo.push(prop);
        }
      });

      setPropiedadesSinDueno(propsSinDueno);
      setPropiedadesSinInmo(propsSinInmo);

      // Identificar inmobiliarias vacías (sin miembros)
      const inmosConMiembros = new Set([
        // Para afiliados, usar el array inmobiliaria_doc_ids
        ...afiliadosConInmo.flatMap(a => a.inmobiliaria_doc_ids || []),
        // Para asociados, usar el string inmobiliaria_doc_id
        ...asociadosConInmo.map(a => a.inmobiliaria_doc_id).filter(id => id)
      ]);

      const inmoVacias = inmos.filter(inmo => !inmosConMiembros.has(inmo.id));
      setInmobiliariasVacias(inmoVacias);

      // Actualizar estadísticas
      setStats({
        afiliadosSinInmo: afiliadosSinInmo.length,
        asociadosSinInmo: asociadosSinInmo.length,
        propsSinDueno: propsSinDueno.length,
        inmobiliariasVacias: inmoVacias.length,
        asignadosHoy: 0
      });

    } catch (error) {
      console.error('Error al cargar datos:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudieron cargar los datos. Inténtalo nuevamente.',
        confirmButtonColor: '#dc3545'
      });
    } finally {
      setLoading(false);
    }
  };

  const asignarMiembroAInmobiliaria = async (miembro: Miembro) => {
    const { value: inmobiliariaId } = await Swal.fire({
      title: 'Asignar a Inmobiliaria',
      html: `
        <div class="text-start mb-3">
          <p><strong>Miembro:</strong> ${miembro.nombre}</p>
          <p><strong>Tipo:</strong> ${miembro.tipo === 'asesor' ? 'Afiliado' : 'Asociado'}</p>
        </div>
        <label for="inmo-select" class="form-label">Selecciona la inmobiliaria:</label>
        <select id="inmo-select" class="form-select">
          <option value="">Seleccionar...</option>
          ${todasInmobiliarias.map(inmo =>
            `<option value="${inmo.id}">${inmo.nombre} ${inmo.ciudad ? `- ${inmo.ciudad}` : ''}</option>`
          ).join('')}
        </select>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Asignar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#28a745',
      preConfirm: () => {
        const select = document.getElementById('inmo-select') as HTMLSelectElement;
        if (!select.value) {
          Swal.showValidationMessage('Debes seleccionar una inmobiliaria');
          return false;
        }
        return select.value;
      }
    });

    if (inmobiliariaId) {
      try {
        const inmoSeleccionada = todasInmobiliarias.find(i => i.id === inmobiliariaId);

        // Actualizar el miembro
        const updates: any = {
          inmobiliaria_nombre: inmoSeleccionada?.nombre || ''
        };

        // Para afiliados (asesor), actualizar el array inmobiliaria_doc_ids
        if (miembro.tipo === 'asesor') {
          updates.inmobiliaria_doc_ids = [inmobiliariaId];
        }
        // Para asociados, actualizar el string inmobiliaria_doc_id
        updates.inmobiliaria_doc_id = inmobiliariaId;

        await updateDoc(doc(db, miembro.tipo, miembro.id), updates);

        Swal.fire({
          icon: 'success',
          title: '¡Asignación Exitosa!',
          html: `
            <div class="text-start">
              <p><strong>${miembro.nombre}</strong> fue asignado a:</p>
              <p class="text-primary">${inmoSeleccionada?.nombre}</p>
              <p class="text-muted mt-2"><small>Las propiedades deben asignarse individualmente según sea necesario</small></p>
            </div>
          `,
          confirmButtonColor: '#28a745'
        });

        setStats(prev => ({ ...prev, asignadosHoy: prev.asignadosHoy + 1 }));
        cargarDatos();
      } catch (error) {
        console.error('Error al asignar:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo completar la asignación.',
          confirmButtonColor: '#dc3545'
        });
      }
    }
  };

  const asignarPropiedadAMiembro = async (propiedad: Propiedad) => {
    const { value: formData } = await Swal.fire({
      title: 'Asignar Propiedad a Miembro',
      html: `
        <div class="text-start mb-3">
          <p><strong>Propiedad:</strong> ${propiedad.titulo}</p>
          <p><strong>Ciudad:</strong> ${propiedad.ciudad || 'N/A'}</p>
        </div>
        <label for="tipo-select" class="form-label">Tipo de miembro:</label>
        <select id="tipo-select" class="form-select mb-3">
          <option value="">Seleccionar...</option>
          <option value="asesor">Afiliado</option>
          <option value="asociado">Asociado</option>
        </select>
        <label for="miembro-select" class="form-label">Miembro:</label>
        <select id="miembro-select" class="form-select" disabled>
          <option value="">Primero selecciona el tipo</option>
        </select>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Asignar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#28a745',
      didOpen: () => {
        const tipoSelect = document.getElementById('tipo-select') as HTMLSelectElement;
        const miembroSelect = document.getElementById('miembro-select') as HTMLSelectElement;

        tipoSelect.addEventListener('change', () => {
          miembroSelect.disabled = false;
          miembroSelect.innerHTML = '<option value="">Seleccionar...</option>';

          const miembros = tipoSelect.value === 'asesor' ? todosAfiliados : todosAsociados;
          miembros.forEach(m => {
            const option = document.createElement('option');
            option.value = m.id;
            option.textContent = `${m.nombre}${m.inmobiliaria_nombre ? ` (${m.inmobiliaria_nombre})` : ''}`;
            // Para afiliados usar el primer elemento del array, para asociados usar el string
            const inmoId = tipoSelect.value === 'asesor'
              ? (m.inmobiliaria_doc_ids && m.inmobiliaria_doc_ids.length > 0 ? m.inmobiliaria_doc_ids[0] : '')
              : (m.inmobiliaria_doc_id || '');
            option.setAttribute('data-inmo', inmoId);
            miembroSelect.appendChild(option);
          });
        });
      },
      preConfirm: () => {
        const tipoSelect = document.getElementById('tipo-select') as HTMLSelectElement;
        const miembroSelect = document.getElementById('miembro-select') as HTMLSelectElement;

        if (!tipoSelect.value || !miembroSelect.value) {
          Swal.showValidationMessage('Debes seleccionar tipo y miembro');
          return false;
        }

        const selectedOption = miembroSelect.options[miembroSelect.selectedIndex];
        return {
          tipo: tipoSelect.value,
          miembroId: miembroSelect.value,
          inmobiliariaId: selectedOption.getAttribute('data-inmo') || ''
        };
      }
    });

    if (formData) {
      try {
        const updates: any = {};

        // Actualizar asesor_doc_ids (array) - agregar el nuevo miembro
        updates.asesor_doc_ids = [formData.miembroId];

        // Mantener campos legacy por compatibilidad
        if (formData.tipo === 'asesor') {
          updates.afiliado_doc_id = formData.miembroId;
          updates.asociado_doc_id = '';
        } else {
          updates.asociado_doc_id = formData.miembroId;
          updates.afiliado_doc_id = '';
        }

        // Si el miembro tiene inmobiliaria, asignarla también
        if (formData.inmobiliariaId) {
          updates.inmobiliaria_doc_id = formData.inmobiliariaId;
        }

        await updateDoc(doc(db, 'propiedades', propiedad.id), updates);

        const miembro = (formData.tipo === 'asesor' ? todosAfiliados : todosAsociados)
          .find(m => m.id === formData.miembroId);

        Swal.fire({
          icon: 'success',
          title: '¡Propiedad Asignada!',
          html: `
            <div class="text-start">
              <p><strong>${propiedad.titulo}</strong> asignada a:</p>
              <p class="text-primary">${miembro?.nombre}</p>
              ${formData.inmobiliariaId ?
                `<p class="text-success mt-2">✅ También asignada a la inmobiliaria del miembro</p>` :
                `<p class="text-warning mt-2">⚠️ El miembro no tiene inmobiliaria asignada</p>`
              }
            </div>
          `,
          confirmButtonColor: '#28a745'
        });

        setStats(prev => ({ ...prev, asignadosHoy: prev.asignadosHoy + 1 }));
        cargarDatos();
      } catch (error) {
        console.error('Error al asignar:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo completar la asignación.',
          confirmButtonColor: '#dc3545'
        });
      }
    }
  };

  const asignarMiembrosAInmoVacia = async (inmobiliaria: Inmobiliaria) => {
    const miembrosDisponibles = miembrosSinInmo;

    if (miembrosDisponibles.length === 0) {
      Swal.fire({
        icon: 'info',
        title: 'No hay miembros disponibles',
        text: 'Todos los miembros ya tienen inmobiliaria asignada.',
        confirmButtonColor: '#17a2b8'
      });
      return;
    }

    const { value: miembrosSeleccionados } = await Swal.fire({
      title: `Asignar miembros a ${inmobiliaria.nombre}`,
      html: `
        <div class="text-start">
          <p class="mb-3">Selecciona los miembros que deseas asignar:</p>
          <div style="max-height: 400px; overflow-y: auto;">
            ${miembrosDisponibles.map((m, index) => `
              <div class="form-check mb-2">
                <input class="form-check-input" type="checkbox" value="${m.id}" id="miembro-${index}" data-tipo="${m.tipo}">
                <label class="form-check-label text-start" for="miembro-${index}">
                  ${m.nombre} <span class="badge bg-${m.tipo === 'asesor' ? 'primary' : 'success'}">${m.tipo === 'asesor' ? 'Afiliado' : 'Asociado'}</span>
                </label>
              </div>
            `).join('')}
          </div>
        </div>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Asignar Seleccionados',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#28a745',
      width: '600px',
      preConfirm: () => {
        const checkboxes = document.querySelectorAll('input[type="checkbox"]:checked');
        const seleccionados: {id: string, tipo: string}[] = [];

        checkboxes.forEach((cb: any) => {
          seleccionados.push({
            id: cb.value,
            tipo: cb.getAttribute('data-tipo')
          });
        });

        if (seleccionados.length === 0) {
          Swal.showValidationMessage('Debes seleccionar al menos un miembro');
          return false;
        }

        return seleccionados;
      }
    });

    if (miembrosSeleccionados && miembrosSeleccionados.length > 0) {
      try {
        const batch = writeBatch(db);

        miembrosSeleccionados.forEach((m: { id: string; tipo: string }) => {
          const miembroRef = doc(db, m.tipo, m.id);
          const updates: any = {
            inmobiliaria_doc_id: inmobiliaria.id,
            inmobiliaria_nombre: inmobiliaria.nombre
          };

          // Para afiliados (asesor), también actualizar el array
          if (m.tipo === 'asesor') {
            updates.inmobiliaria_doc_ids = [inmobiliaria.id];
          }

          batch.update(miembroRef, updates);
        });

        await batch.commit();

        Swal.fire({
          icon: 'success',
          title: '¡Asignación Masiva Exitosa!',
          html: `
            <div class="text-start">
              <p><strong>${miembrosSeleccionados.length}</strong> miembro(s) asignado(s) a:</p>
              <p class="text-primary">${inmobiliaria.nombre}</p>
            </div>
          `,
          confirmButtonColor: '#28a745'
        });

        setStats(prev => ({ ...prev, asignadosHoy: prev.asignadosHoy + miembrosSeleccionados.length }));
        cargarDatos();
      } catch (error) {
        console.error('Error al asignar:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo completar la asignación.',
          confirmButtonColor: '#dc3545'
        });
      }
    }
  };

  const asignarPropiedadAInmobiliaria = async (propiedad: Propiedad) => {
    // Buscar si la propiedad tiene dueño
    let inmoSugerida: Inmobiliaria | undefined = undefined;
    let dueno: any = null;

    if (propiedad.afiliado_doc_id) {
      const afiliadoDoc = await getDoc(doc(db, 'asesor', propiedad.afiliado_doc_id));
      if (afiliadoDoc.exists()) {
        dueno = afiliadoDoc.data();
        // Para afiliados, verificar el array inmobiliaria_doc_ids primero
        if (dueno.inmobiliaria_doc_ids && Array.isArray(dueno.inmobiliaria_doc_ids) && dueno.inmobiliaria_doc_ids.length > 0) {
          inmoSugerida = todasInmobiliarias.find(i => i.id === dueno.inmobiliaria_doc_ids[0]);
        } else if (dueno.inmobiliaria_doc_id) {
          // Fallback al campo legacy
          inmoSugerida = todasInmobiliarias.find(i => i.id === dueno.inmobiliaria_doc_id);
        }
      }
    } else if (propiedad.asociado_doc_id) {
      const asociadoDoc = await getDoc(doc(db, 'asociado', propiedad.asociado_doc_id));
      if (asociadoDoc.exists()) {
        dueno = asociadoDoc.data();
        // Para asociados, usar el string inmobiliaria_doc_id
        if (dueno.inmobiliaria_doc_id) {
          inmoSugerida = todasInmobiliarias.find(i => i.id === dueno.inmobiliaria_doc_id);
        }
      }
    }

    const { value: inmobiliariaId } = await Swal.fire({
      title: 'Asignar a Inmobiliaria',
      html: `
        <div class="text-start mb-3">
          <p><strong>Propiedad:</strong> ${propiedad.titulo}</p>
          ${dueno ? `<p><strong>Dueño:</strong> ${dueno.nombre}</p>` : ''}
          ${inmoSugerida ? `
            <div class="alert alert-info">
              <strong>💡 Sugerencia:</strong> El dueño pertenece a <strong>${inmoSugerida.nombre}</strong>
            </div>
          ` : ''}
        </div>
        <label for="inmo-select" class="form-label">Selecciona la inmobiliaria:</label>
        <select id="inmo-select" class="form-select">
          ${inmoSugerida ?
            `<option value="${inmoSugerida.id}" selected>${inmoSugerida.nombre} (Sugerida)</option>` :
            '<option value="">Seleccionar...</option>'
          }
          ${todasInmobiliarias.map(inmo =>
            inmo.id === inmoSugerida?.id ? '' :
            `<option value="${inmo.id}">${inmo.nombre} ${inmo.ciudad ? `- ${inmo.ciudad}` : ''}</option>`
          ).join('')}
        </select>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Asignar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#28a745',
      preConfirm: () => {
        const select = document.getElementById('inmo-select') as HTMLSelectElement;
        if (!select.value) {
          Swal.showValidationMessage('Debes seleccionar una inmobiliaria');
          return false;
        }
        return select.value;
      }
    });

    if (inmobiliariaId) {
      try {
        await updateDoc(doc(db, 'propiedades', propiedad.id), {
          inmobiliaria_doc_id: inmobiliariaId
        });

        const inmoSeleccionada = todasInmobiliarias.find(i => i.id === inmobiliariaId);

        Swal.fire({
          icon: 'success',
          title: '¡Propiedad Asignada!',
          html: `
            <div class="text-start">
              <p><strong>${propiedad.titulo}</strong> asignada a:</p>
              <p class="text-primary">${inmoSeleccionada?.nombre}</p>
            </div>
          `,
          confirmButtonColor: '#28a745'
        });

        setStats(prev => ({ ...prev, asignadosHoy: prev.asignadosHoy + 1 }));
        cargarDatos();
      } catch (error) {
        console.error('Error al asignar:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo completar la asignación.',
          confirmButtonColor: '#dc3545'
        });
      }
    }
  };

  // Asignación automática masiva de propiedades a inmobiliarias basada en sus asesores
  const asignarAutomaticamenteInmobiliarias = async () => {
    const resultado = await Swal.fire({
      title: '🤖 Asignación Automática',
      html: `
        <div class="text-start">
          <p>Esta función analizará todas las propiedades sin inmobiliaria y:</p>
          <ol>
            <li>Verificará si tienen asesores asignados</li>
            <li>Consultará la inmobiliaria de cada asesor</li>
            <li>Asignará automáticamente la inmobiliaria a la propiedad</li>
          </ol>
          <div class="alert alert-warning mt-3">
            <strong>⚠️ Advertencia:</strong> Esta acción actualizará múltiples propiedades.
          </div>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, asignar automáticamente',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#6c757d'
    });

    if (!resultado.isConfirmed) return;

    try {
      Swal.fire({
        title: 'Procesando...',
        html: 'Analizando propiedades y asesores',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      let procesadas = 0;
      let asignadas = 0;
      let sinAsesor = 0;
      let asesorSinInmo = 0;

      const batch = writeBatch(db);

      for (const prop of propiedadesSinInmo) {
        procesadas++;

        // Obtener documento de la propiedad para verificar asesor_doc_ids
        const propDocRef = doc(db, 'propiedades', prop.id);
        const propSnap = await getDoc(propDocRef);

        if (!propSnap.exists()) continue;

        const propData = propSnap.data();
        const asesorDocIds = propData.asesor_doc_ids;

        // Verificar si tiene asesores asignados
        if (!asesorDocIds || !Array.isArray(asesorDocIds) || asesorDocIds.length === 0) {
          sinAsesor++;
          continue;
        }

        // Tomar el primer asesor
        const primerAsesorId = asesorDocIds[0];

        // Buscar el asesor en ambas colecciones: 'asesor' (afiliados) y 'asociado'
        let asesorDoc = await getDoc(doc(db, 'asesor', primerAsesorId));

        // Si no está en asesores (afiliados), buscar en asociados
        if (!asesorDoc.exists()) {
          asesorDoc = await getDoc(doc(db, 'asociado', primerAsesorId));
        }

        if (!asesorDoc.exists()) {
          sinAsesor++;
          continue;
        }

        const asesorData = asesorDoc.data();
        let inmobiliariaId = '';

        // Determinar el ID de la inmobiliaria según el tipo de miembro
        // Si viene de la colección 'asesor' (afiliados), usar el array inmobiliaria_doc_ids
        // Si viene de la colección 'asociado', usar el string inmobiliaria_doc_id
        if (asesorData.inmobiliaria_doc_ids && Array.isArray(asesorData.inmobiliaria_doc_ids) && asesorData.inmobiliaria_doc_ids.length > 0) {
          inmobiliariaId = asesorData.inmobiliaria_doc_ids[0];
        } else if (asesorData.inmobiliaria_doc_id) {
          inmobiliariaId = asesorData.inmobiliaria_doc_id;
        }

        // Verificar si el asesor tiene inmobiliaria
        if (!inmobiliariaId || inmobiliariaId === '') {
          asesorSinInmo++;
          continue;
        }

        // Asignar la inmobiliaria a la propiedad
        batch.update(propDocRef, {
          inmobiliaria_doc_id: inmobiliariaId
        });

        asignadas++;
      }

      // Ejecutar todas las actualizaciones
      if (asignadas > 0) {
        await batch.commit();
      }

      // Recargar datos
      await cargarDatos();

      // Mostrar resultado
      Swal.fire({
        icon: asignadas > 0 ? 'success' : 'info',
        title: 'Proceso Completado',
        html: `
          <div class="text-start">
            <p><strong>📊 Resumen:</strong></p>
            <ul>
              <li>✅ Propiedades procesadas: <strong>${procesadas}</strong></li>
              <li>🎯 Inmobiliarias asignadas: <strong class="text-success">${asignadas}</strong></li>
              <li>⚠️ Propiedades sin asesor: <strong>${sinAsesor}</strong></li>
              <li>⚠️ Asesores sin inmobiliaria: <strong>${asesorSinInmo}</strong></li>
            </ul>
            ${asignadas > 0 ?
              '<p class="text-success mb-0">✅ Las propiedades se actualizaron correctamente</p>' :
              '<p class="text-warning mb-0">⚠️ No se encontraron propiedades para asignar automáticamente</p>'
            }
          </div>
        `,
        confirmButtonColor: '#28a745'
      });

    } catch (error) {
      console.error('Error en asignación automática:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Ocurrió un error durante el proceso de asignación automática.',
        confirmButtonColor: '#dc3545'
      });
    }
  };

  // Filtros de búsqueda
  const filtrarDatos = (datos: any[], campos: string[]) => {
    if (!busqueda.trim()) return datos;

    const searchLower = busqueda.toLowerCase();
    return datos.filter(item =>
      campos.some(campo => {
        const valor = campo.split('.').reduce((obj, key) => obj?.[key], item);
        return valor && String(valor).toLowerCase().includes(searchLower);
      })
    );
  };

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
        <p className="mt-3 text-muted">Cargando datos de asignaciones...</p>
      </div>
    );
  }

  if (!user || !user.tipo?.includes('admin')) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger">
          <h4>Acceso Denegado</h4>
          <p>No tienes permiso para acceder a esta sección</p>
          <button className="btn btn-primary" onClick={() => navigate('/admin-dashboard')}>
            Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }

  const miembrosFiltrados = filtrarDatos(miembrosSinInmo, ['nombre', 'correo', 'telefono']);
  const propsSinDuenoFiltradas = filtrarDatos(propiedadesSinDueno, ['titulo', 'nombre', 'list_number', 'ciudad']);
  const inmosVaciasFiltradas = filtrarDatos(inmobiliariasVacias, ['nombre', 'ciudad']);
  const propsSinInmoFiltradas = filtrarDatos(propiedadesSinInmo, ['titulo', 'nombre', 'list_number', 'ciudad']);

  return (
    <>
      <UserNav />

      <div className="page-title">
        <div className="container">
          <h2 className="ipt-title">Gestión de Asignaciones</h2>
          <span className="ipn-subtitle">Herramienta administrativa para vincular entidades</span>
        </div>
      </div>

      <section className="gray-simple">
        <div className="container">
          <Breadcrumb
            items={[
              { label: 'Dashboard', path: '/admin-dashboard' },
              { label: 'Herramientas Administrativas' },
              { label: 'Asignaciones' }
            ]}
          />

          {/* Estadísticas Generales */}
          <div className="row g-3 mb-4">
            <div className="col-md-3 col-sm-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="flex-shrink-0">
                      <div className="bg-primary bg-opacity-10 p-3 rounded">
                        <Users className="text-primary" size={24} />
                      </div>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <h6 className="text-muted mb-0">Afiliados Sin Inmo.</h6>
                      <h3 className="mb-0">{stats.afiliadosSinInmo}</h3>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-3 col-sm-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="flex-shrink-0">
                      <div className="bg-success bg-opacity-10 p-3 rounded">
                        <Users className="text-success" size={24} />
                      </div>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <h6 className="text-muted mb-0">Asociados Sin Inmo.</h6>
                      <h3 className="mb-0">{stats.asociadosSinInmo}</h3>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-3 col-sm-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="flex-shrink-0">
                      <div className="bg-warning bg-opacity-10 p-3 rounded">
                        <Home className="text-warning" size={24} />
                      </div>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <h6 className="text-muted mb-0">Props Sin Dueño</h6>
                      <h3 className="mb-0">{stats.propsSinDueno}</h3>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-3 col-sm-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="flex-shrink-0">
                      <div className="bg-info bg-opacity-10 p-3 rounded">
                        <Building2 className="text-info" size={24} />
                      </div>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <h6 className="text-muted mb-0">Inmo. Vacías</h6>
                      <h3 className="mb-0">{stats.inmobiliariasVacias}</h3>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white">
              <ul className="nav nav-tabs card-header-tabs">
                <li className="nav-item">
                  <button
                    className={`nav-link ${tabActiva === 'miembros-inmo' ? 'active' : ''}`}
                    onClick={() => setTabActiva('miembros-inmo')}
                  >
                    <ArrowRightLeft size={16} className="me-2" />
                    Miembros → Inmo
                    {miembrosSinInmo.length > 0 && (
                      <span className="badge bg-danger ms-2">{miembrosSinInmo.length}</span>
                    )}
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${tabActiva === 'props-miembros' ? 'active' : ''}`}
                    onClick={() => setTabActiva('props-miembros')}
                  >
                    <ArrowRightLeft size={16} className="me-2" />
                    Props → Miembros
                    {propiedadesSinDueno.length > 0 && (
                      <span className="badge bg-danger ms-2">{propiedadesSinDueno.length}</span>
                    )}
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${tabActiva === 'miembros-vacio' ? 'active' : ''}`}
                    onClick={() => setTabActiva('miembros-vacio')}
                  >
                    <ArrowRightLeft size={16} className="me-2" />
                    Miembros → Inmo Vacías
                    {inmobiliariasVacias.length > 0 && (
                      <span className="badge bg-danger ms-2">{inmobiliariasVacias.length}</span>
                    )}
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${tabActiva === 'props-inmo' ? 'active' : ''}`}
                    onClick={() => setTabActiva('props-inmo')}
                  >
                    <ArrowRightLeft size={16} className="me-2" />
                    Props → Inmo
                    {propiedadesSinInmo.length > 0 && (
                      <span className="badge bg-danger ms-2">{propiedadesSinInmo.length}</span>
                    )}
                  </button>
                </li>
              </ul>
            </div>

            <div className="card-body">
              {/* Barra de búsqueda */}
              <div className="row mb-4">
                <div className="col-md-6">
                  <div className="input-group">
                    <span className="input-group-text bg-white">
                      <Search size={18} />
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Buscar..."
                      value={busqueda}
                      onChange={(e) => setBusqueda(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-md-6 text-end">
                  <div className="badge bg-success fs-6">
                    <CheckCircle size={16} className="me-1" />
                    Asignados hoy: {stats.asignadosHoy}
                  </div>
                </div>
              </div>

              {/* Contenido de cada tab */}
              {tabActiva === 'miembros-inmo' && (
                <div>
                  <h5 className="mb-3">
                    <Users size={20} className="me-2" />
                    Miembros sin Inmobiliaria ({miembrosFiltrados.length})
                  </h5>
                  {miembrosFiltrados.length === 0 ? (
                    <div className="alert alert-success">
                      <CheckCircle size={20} className="me-2" />
                      ¡Excelente! Todos los miembros tienen inmobiliaria asignada.
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead className="table-light">
                          <tr>
                            <th>Nombre</th>
                            <th>Tipo</th>
                            <th>Correo</th>
                            <th>Teléfono</th>
                            <th>Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {miembrosFiltrados.map(miembro => (
                            <tr key={miembro.id}>
                              <td>{miembro.nombre}</td>
                              <td>
                                <span className={`badge bg-${miembro.tipo === 'asesor' ? 'primary' : 'success'}`}>
                                  {miembro.tipo === 'asesor' ? 'Afiliado' : 'Asociado'}
                                </span>
                              </td>
                              <td>{miembro.correo}</td>
                              <td>{miembro.telefono}</td>
                              <td>
                                <button
                                  className="btn btn-sm btn-primary"
                                  onClick={() => asignarMiembroAInmobiliaria(miembro)}
                                >
                                  <LinkIcon size={14} className="me-1" />
                                  Asignar
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {tabActiva === 'props-miembros' && (
                <div>
                  <h5 className="mb-3">
                    <Home size={20} className="me-2" />
                    Propiedades sin Dueño ({propsSinDuenoFiltradas.length})
                  </h5>
                  {propsSinDuenoFiltradas.length === 0 ? (
                    <div className="alert alert-success">
                      <CheckCircle size={20} className="me-2" />
                      ¡Excelente! Todas las propiedades tienen dueño asignado.
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead className="table-light">
                          <tr>
                            <th>Título</th>
                            <th>List Number</th>
                            <th>Ciudad</th>
                            <th>Precio</th>
                            <th>Tipo</th>
                            <th>Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {propsSinDuenoFiltradas.map(prop => (
                            <tr key={prop.id}>
                              <td>{prop.titulo}</td>
                              <td><code>{prop.list_number || 'N/A'}</code></td>
                              <td>{prop.ciudad}</td>
                              <td>${prop.precio?.toLocaleString('es-MX') || 0}</td>
                              <td>{prop.tipo}</td>
                              <td>
                                <button
                                  className="btn btn-sm btn-primary"
                                  onClick={() => asignarPropiedadAMiembro(prop)}
                                >
                                  <LinkIcon size={14} className="me-1" />
                                  Asignar
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {tabActiva === 'miembros-vacio' && (
                <div>
                  <h5 className="mb-3">
                    <Building2 size={20} className="me-2" />
                    Inmobiliarias sin Miembros ({inmosVaciasFiltradas.length})
                  </h5>
                  {inmosVaciasFiltradas.length === 0 ? (
                    <div className="alert alert-success">
                      <CheckCircle size={20} className="me-2" />
                      ¡Excelente! Todas las inmobiliarias tienen miembros asignados.
                    </div>
                  ) : (
                    <div className="row g-3">
                      {inmosVaciasFiltradas.map(inmo => (
                        <div key={inmo.id} className="col-md-6">
                          <div className="card border-warning">
                            <div className="card-body">
                              <h6 className="card-title">{inmo.nombre}</h6>
                              <p className="card-text small text-muted mb-2">
                                {inmo.ciudad && <><i className="bi bi-geo-alt"></i> {inmo.ciudad}<br /></>}
                                {inmo.telefono && <><i className="bi bi-telephone"></i> {inmo.telefono}</>}
                              </p>
                              <button
                                className="btn btn-sm btn-warning"
                                onClick={() => asignarMiembrosAInmoVacia(inmo)}
                              >
                                <Users size={14} className="me-1" />
                                Asignar Miembros
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {tabActiva === 'props-inmo' && (
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">
                      <Home size={20} className="me-2" />
                      Propiedades sin Inmobiliaria ({propsSinInmoFiltradas.length})
                    </h5>
                    {propsSinInmoFiltradas.length > 0 && (
                      <button
                        className="btn btn-success"
                        onClick={asignarAutomaticamenteInmobiliarias}
                      >
                        <Zap size={16} className="me-2" />
                        Asignación Automática
                      </button>
                    )}
                  </div>
                  {propsSinInmoFiltradas.length === 0 ? (
                    <div className="alert alert-success">
                      <CheckCircle size={20} className="me-2" />
                      ¡Excelente! Todas las propiedades tienen inmobiliaria asignada.
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead className="table-light">
                          <tr>
                            <th>Título</th>
                            <th>List Number</th>
                            <th>Dueño Actual</th>
                            <th>Ciudad</th>
                            <th>Precio</th>
                            <th>Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {propsSinInmoFiltradas.map(prop => {
                            const tieneDueno = prop.afiliado_doc_id || prop.asociado_doc_id;
                            return (
                              <tr key={prop.id}>
                                <td>{prop.titulo}</td>
                                <td><code>{prop.list_number || 'N/A'}</code></td>
                                <td>
                                  {tieneDueno ? (
                                    <span className="badge bg-info">Tiene dueño</span>
                                  ) : (
                                    <span className="badge bg-secondary">Sin dueño</span>
                                  )}
                                </td>
                                <td>{prop.ciudad}</td>
                                <td>${prop.precio?.toLocaleString('es-MX') || 0}</td>
                                <td>
                                  <button
                                    className="btn btn-sm btn-primary"
                                    onClick={() => asignarPropiedadAInmobiliaria(prop)}
                                  >
                                    <LinkIcon size={14} className="me-1" />
                                    Asignar
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Información adicional */}
          <div className="card border-0 shadow-sm mt-4">
            <div className="card-body">
              <h6 className="card-title">
                <AlertCircle size={18} className="me-2 text-info" />
                Información Importante
              </h6>
              <ul className="mb-0 small text-muted">
                <li>Las propiedades deben asignarse individualmente a los miembros según sea necesario</li>
                <li>Al asignar una propiedad a un miembro, si el miembro tiene inmobiliaria, la propiedad se asignará a esa inmobiliaria también</li>
                <li>Los cambios son permanentes y se reflejan inmediatamente en todas las páginas del sistema</li>
                <li>Se recomienda revisar las sugerencias del sistema antes de realizar asignaciones manuales</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
